import { useMemo } from "react";
import { css } from "@emotion/css";
import { Box, Divider, Drawer, IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";
import { grey } from "@mui/material/colors";
import { DataGrid } from "@mui/x-data-grid";
import { CompanyLogo, Icons, Loading } from "@analyticsMD/quil";

import SearchBar from "../filter/search-bar";
import QuickFilter from "../../utils/filter/quick-filter";
import { Sizes } from "../../types/loadingSizes";

import type { FC, ReactNode } from "react";
import type { SxProps, Theme } from "@mui/material/styles";
import type { DataGridProps, GridRowId } from "@mui/x-data-grid";
import type { SearchBarProps } from "../filter/search-bar";

export const ROW_HOVER_BG = "#EFF6FF";
export const ROW_HOVER_ACCENT = "#1B76D3";

const sxStyles = {
  headerWrapper: {
    display: "flex",
    alignItems: "center",
    background: grey["A100"],
    height: "4rem",
    marginBottom: "1rem",
    gap: "1.5rem",
  },
  logo: {
    width: "6.5rem",
    height: "2.5rem",
    paddingLeft: "1.37rem",
  },
  table: {
    "& .MuiDataGrid-columnHeader": { background: grey[200] },
    "& .MuiDataGrid-columnHeaderTitle": {
      textTransform: "uppercase",
      fontWeight: "bold",
      color: grey[600],
    },
    "& .MuiDataGrid-columnSeparator": {
      color: "transparent",
    },
    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": { py: "15px" },
    "& .MuiDataGrid-cell": {
      whiteSpace: "normal",
      wordWrap: "break-word",
      display: "flex",
      alignItems: "flex-start",
    },
    "& .MuiDataGrid-filler": {
      backgroundColor: grey[200] + " !important",
    },
    "& .MuiDataGrid-cell:focus": {
      outline: "none",
    },
    "& .MuiDataGrid-cell:focus-within": {
      outline: "none",
    },
    "& .MuiDataGrid-columnHeader:focus": {
      outline: "none",
    },
    "& .MuiDataGrid-columnHeader:focus-within": {
      outline: "none",
    },
    "& .MuiDataGrid-row": {
      "&:hover": {
        backgroundColor: ROW_HOVER_BG,
        boxShadow: `inset 4px 0 0 ${ROW_HOVER_ACCENT}`,
      },
      // Selected row (card open): match the hover highlight, not default gray
      "&.Mui-selected": {
        backgroundColor: `${ROW_HOVER_BG}`,
        boxShadow: `inset 4px 0 0 ${ROW_HOVER_ACCENT}`,

        "&:hover": {
          backgroundColor: `${ROW_HOVER_BG}`,
          boxShadow: `inset 4px 0 0 ${ROW_HOVER_ACCENT}`,
        },
      },
    },
    "& .MuiDataGrid-overlayWrapperInner": { backgroundColor: "white" },
  },
  overlay: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  filterPanel: {
    paddingBottom: "1rem",
    paddingLeft: "2rem",
    paddingTop: "0.3rem",
    marginRight: "-1.5rem",
    width: "18rem",
    alignSelf: "flex-start",
  },
  filterPanelWrapper: {
    display: "flex",
    alignItems: "baseline",
    width: "100%",
    marginBottom: "1rem",
  },
  drawer: {
    pointerEvents: "none",
    "& .MuiDialog-container": {
      pointerEvents: "none",
    },
    "& .MuiPaper-root": {
      pointerEvents: "auto",
    },
    "& .MuiDrawer-paper": {
      width: "31.25rem",
    },
  },
  closeButton: {
    color: "white",
    alignSelf: "flex-start",
    cursor: "pointer",
  },
};

const styles = {
  noRowsIcon: css`
    height: 15.625rem;
    width: 100%;
  `,
  noRowsText: css`
    text-align: center;
    font-size: 1.1rem;
    color: ${grey[800]};
  `,
};

const LoadingGridOverlay: FC = () => (
  <Box sx={sxStyles.overlay}>
    <Box>
      <Loading message="Loading..." logoSize={Sizes.Medium} />
    </Box>
  </Box>
);

const NoRowsOverlay: FC<{ message: string }> = ({ message }) => (
  <Box sx={sxStyles.overlay}>
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Icons.NoresultsIcon className={styles.noRowsIcon} />
      <span className={styles.noRowsText}>{message}</span>
    </Box>
  </Box>
);

export interface ListDrawerProps {
  isOpen: boolean;
  rowId: GridRowId | null;
  onClose: () => void;
  renderContent: (closeButton: ReactNode) => ReactNode;
}

interface ListPageShellProps {
  banner?: ReactNode;
  viewSelector: ReactNode;
  headerActions?: ReactNode;
  filters?: ReactNode;
  search?: SearchBarProps;
  noRowsMessage?: string;
  gridProps: DataGridProps;
  gridSx?: SxProps<Theme>;
  drawer: ListDrawerProps;
  children?: ReactNode;
}

// The layout the QList and the malnutrition list share: header with the View
// picker, search and filters, the patient grid, and the card drawer. Pages own
// their data, columns and what clicking a row does.
const ListPageShell: FC<ListPageShellProps> = ({
  banner,
  viewSelector,
  headerActions,
  filters,
  search,
  noRowsMessage = "No patients found",
  gridProps,
  gridSx,
  drawer,
  children,
}) => {
  // Columns without an explicit `sortable` take part in the quick search,
  // with their own matcher if they declare one.
  const columns = useMemo(
    () =>
      gridProps.columns.map((c) =>
        c.sortable === undefined && !c.getApplyQuickFilterFn
          ? { ...c, getApplyQuickFilterFn: QuickFilter }
          : c
      ),
    [gridProps.columns]
  );

  const { variant, placeholder } = search ?? {};
  const slots = useMemo(
    () => ({
      loadingOverlay: LoadingGridOverlay,
      noRowsOverlay: () => <NoRowsOverlay message={noRowsMessage} />,
      toolbar: () => <SearchBar variant={variant} placeholder={placeholder} />,
    }),
    [noRowsMessage, variant, placeholder]
  );

  const closeButton = (
    <IconButton
      onClick={drawer.onClose}
      size="small"
      aria-label="Close"
      sx={sxStyles.closeButton}
    >
      <Close />
    </IconButton>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {banner}
      <Box sx={sxStyles.headerWrapper}>
        <CompanyLogo className={css(sxStyles.logo)} useDarkVersion />
        <Divider
          orientation="vertical"
          flexItem
          sx={{ height: "2.625rem", alignSelf: "center" }}
        />
        {viewSelector}
        {headerActions}
      </Box>
      <Box sx={sxStyles.filterPanelWrapper}>
        <Box id="filter-panel" sx={sxStyles.filterPanel} />
        {filters}
      </Box>
      <DataGrid
        {...gridProps}
        columns={columns}
        disableDensitySelector
        disableColumnResize
        disableColumnMenu
        sx={
          gridSx
            ? [sxStyles.table, ...(Array.isArray(gridSx) ? gridSx : [gridSx])]
            : sxStyles.table
        }
        showToolbar
        slots={slots}
      />
      {children}
      <Drawer
        key={`drawer-${drawer.rowId || 0}`}
        anchor="right"
        open={drawer.isOpen}
        onClose={drawer.onClose}
        hideBackdrop
        disableEnforceFocus
        disableAutoFocus
        ModalProps={{
          sx: sxStyles.drawer,
        }}
      >
        {drawer.renderContent(closeButton)}
      </Drawer>
    </Box>
  );
};

export default ListPageShell;
